import { BaseOption } from 'src/app/model/BaseOption';
import { FontTypeEnum } from 'src/app/model/enums/FontTypeEnum';
import { ConfigSettingDto } from '../../../model/business/ConfigSettingDto';
import { CalculationRequestDto } from '../../../model/business/CalculationRequestDto';
import { QuotationRequestDto } from '../../../model/business/calculation/QuotationRequestDto';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { EnumConverterService } from 'src/app/services/EnumConverterService';
import { OptionsEnum } from 'src/app/model/enums/OptionsEnum';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CoverTypeEnum } from 'src/app/model/enums/CoverTypeEnum';
import { CalculationApiService } from 'src/app/services/calculationApiService';
import { InsuredAttributesDto } from 'src/app/model/business/calculation/InsuredAttributesDto';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { CalculationResponseResultPremiumDto } from 'src/app/model/business/calculation/CalculationResponseResultPremiumDto';
import { SessionService } from 'src/app/session-service.service';
import { DateConverterService } from 'src/app/date-converter.service';
import { InsurerEnum } from 'src/app/model/enums/InsurerEnum';
import { ComponentCommunicationService } from 'src/app/component-communication.service';
import { Offer } from 'src/app/model/Offer';
import { addMonths, startOfMonth } from 'date-fns';
import { formatDate } from '@angular/common';
import { AvailablePremiumsPipe } from 'src/app/available-premiums.pipe';
import NavigationSettings, { StepsORV } from 'src/app/components/navigation-component/navigation-settings.service';
import { NOTIFICATION_TYPE, NotificationEvent, NotificationService } from 'src/app/services/NotificationService';
import { ActivatedRoute } from '@angular/router';
import { ValidationService } from '../../../services/ValidationService';
import { openPDFFromByteCharacters} from '../../../utils/utils';
import { ProductTypeEnum } from '../../../model/enums/ProductTypeEnum';
import { SessionOrvService } from '../../../services/session-orv.service';
import { PolicyTerms } from '../../../model/enums/PolicyTermsEnum';
import { Constants } from '../../../constants';
import { replaceThousandSeparatorAndRemovingEuroPrefix } from '../../../utils/utils';
import { Subscription } from 'rxjs';

type ShowFormValidityOptions = {
  showResult?: boolean;
};

@Component({
  selector: 'app-calculate-page-orv',
  templateUrl: './calculation-page.component.html',
  styleUrls: ['./calculation-page.component.css'],
})
export class CalculationPageComponent implements OnInit, OnDestroy {
  MONTHLY_PAYMENT_METHOD = 'MONTHLY';
  displayedColumns = ['insurer', 'premium'];
  premiumList = [];
  offersDataSource: Offer[] = null;
  filteredOffersDataSource: Offer[] = null;
  intermediaryId: string;
  configSettingsDto: ConfigSettingDto;
  formGroup: FormGroup;
  FontTypeEnum = FontTypeEnum;
  secondInsuredPresent: string;
  minDate = startOfMonth(addMonths(new Date(), 1));
  maximumMonths = 6;
  datesArray: BaseOption[] = [];
  yesNoList = this.enumConverterService.toBaseList('OptionsEnum', OptionsEnum);
  coverTypeList = this.enumConverterService.toBaseList('CoverTypeEnum', CoverTypeEnum);
  premiumsInsurerList = this.enumConverterService.toBaseList('InsurerEnum', InsurerEnum);
  additionalInfoInsurer: string;
  annuityFormat: string;
  OptionsEnum = OptionsEnum;
  CoverTypeEnum = CoverTypeEnum;
  premiums: CalculationResponseResultPremiumDto;
  selectedBestPremium = {insurer: '', premium: '', policyTerms: ''};
  title = 'Calculation Page';
  locale: string;
  notificationEvent: NotificationEvent;
  notificationSubscription: Subscription;
  relativePath: string;
  productType = ProductTypeEnum.ORV;

  thousandSeparator = Constants.THOUSAND_SEPARATOR;

  constructor(
    private enumConverterService: EnumConverterService,
    private calculationApiService: CalculationApiService,
    private fb: FormBuilder,
    public translationService: TranslateService,
    private sessionService: SessionService,
    private sessionOrvService: SessionOrvService,
    private dateConverterService: DateConverterService,
    private componentCommunicationService: ComponentCommunicationService,
    private navigationSettings: NavigationSettings,
    private availablePremiumsPipe: AvailablePremiumsPipe,
    private notificationService: NotificationService,
    private activatedRoute: ActivatedRoute,
    private validationService: ValidationService,
  ) {
    const {
      productType,
      locale,
    } = this.activatedRoute.snapshot.params;

    this.relativePath = locale ?
    `/${locale}/${productType}` :
    `/${productType}`;
  }

  ngOnInit(): void {
    const maxAmountAssured = 750000;

    const {
      secondInsuredPresent,
      typeOfCover,
      annuityRate,
      numberOfYears,
      premiums,
      insured: {
        sumAssured,
        dateOfBirth,
        isSmoker,
      },
      secondInsured: {
        sumAssured: sumAssured2,
        dateOfBirth: dateOfBirth2,
        isSmoker: isSmoker2,
      }
    } = this.sessionOrvService.getCalculationFormValues();

    let {
      dateOfCommencement,
    } = this.sessionOrvService.getCalculationFormValues();

    if (!dateOfCommencement) {
      dateOfCommencement = this.minDate;
    }

    const insured = this.fb.group({
      sumAssured: [sumAssured, [Validators.required, this.validationService.maxSumAssured(maxAmountAssured, this.thousandSeparator)]],
      dateOfBirth: [dateOfBirth, [Validators.required, this.validationService.createDateOfBirthValidator()]],
      isSmoker: [isSmoker, Validators.required],
    });

    const secondInsured = this.fb.group({
      sumAssured: [sumAssured2, this.validationService.maxSumAssured(maxAmountAssured, this.thousandSeparator)],
      dateOfBirth: [dateOfBirth2, [Validators.required, this.validationService.createDateOfBirthValidator()]],
      isSmoker: [isSmoker2, Validators.required],
    });

    this.formGroup = this.fb.group({
      insured,
      secondInsuredPresent: [secondInsuredPresent, Validators.required],
      secondInsured,
      typeOfCover: [typeOfCover, Validators.required],
      annuityRate: [annuityRate, [
          Validators.required,
          this.validationService.createAnnuityRateValidator(ValidationService.MAX_ANNUITY_RATE),
      ]],
      dateOfCommencement: [dateOfCommencement, Validators.required],
      numberOfYears: [numberOfYears, [
          Validators.required,
          Validators.min(1),
          this.validationService.createCoverTermLengthValidator(ValidationService.MAX_COVER_LENGTH_IN_YEARS)]],
      premiums: [premiums],
    });

    this.locale = this.translationService.currentLang || this.translationService.defaultLang;
    this.translationService.onDefaultLangChange.subscribe((e: LangChangeEvent) => {
        this.locale = e.lang;
      });

    for (let i = 0; i < this.maximumMonths; i++) {
      const date = startOfMonth(addMonths(this.minDate, i));
      this.datesArray.push({
        text: formatDate(date, 'longDate', this.locale),
        value: date,
      });
    }

    if (this.isFormValid({ showResult: false })) {
      this.doCalculation();
    }
    this.notificationSubscription = this.notificationService.register().subscribe((event) => {
      this.notificationEvent = event;
    });
  }

  ngOnDestroy(): void {
    this.notificationSubscription.unsubscribe();
  }

  setActiveStep(): void {
    this.navigationSettings.setActiveState(StepsORV.CALCULATE, true);
  }

  paramsHaveUpdated(): void {
    if (this.isSecondInsured() && this.formGroup.controls.secondInsured.status !== 'VALID') {
      return;
    }

    if (this.isPremiumsCalculated()) {
      this.doCalculation();
    }
  }

  getSelectPremium(event): void {
    this.premiums = event;
    const premiumInsurer = this.getInsurerTranslation(event.insurer);
    this.selectedBestPremium.insurer = premiumInsurer.text;
    this.selectedBestPremium.premium = event.premium;

    this.filteredOffersDataSource = this.availablePremiumsPipe.transform(this.offersDataSource, this.premiums.insurer);
  }

  isFormValid(options: ShowFormValidityOptions = { showResult: true }): boolean {
    if (
      this.formGroup.get('insured').valid &&
      (this.formGroup.value.secondInsuredPresent === OptionsEnum.No ||
        this.formGroup.get('secondInsured').valid) &&
      this.isCoverValid() &&
      this.formGroup.get('numberOfYears').valid &&
      this.formGroup.get('dateOfCommencement').valid
    ) {
      return true;
    }

    if (options.showResult) {
      this.formGroup.markAllAsTouched();
      this.formGroup.get('insured').markAllAsTouched();
      if (this.formGroup.value.secondInsuredPresent) {
        this.formGroup.get('secondInsured').markAllAsTouched();
      }

      this.translationService.get('Common.ValidationFailedMessage').subscribe((message) => {
          this.notificationEvent = {
            type: NOTIFICATION_TYPE.WARNING,
            messages: [message],
          };
        });
    }

    return false;
  }

  /**
   * Validates that all inputs related to the chosen type of cover are indeed valid
   */
  isCoverValid(): boolean {
    if (!this.formGroup.get('typeOfCover').valid) {
      return false;
    }
    if (this.formGroup.value.typeOfCover === CoverTypeEnum.ANNUITY_DECREASING) {
      if (!this.formGroup.get('annuityRate').valid) {
        return false;
      }
    }

    return true;
  }

  get configSettings(): ConfigSettingDto {
    return this.sessionService.getConfigSettingsDto();
  }

  private setupInsured(
    calculationRequestDto: CalculationRequestDto,
    insuredControl: any
  ): void {
    const insured = new InsuredAttributesDto();
    this.minDate = this.formGroup.value.dateOfCommencement;
    insured.dateOfBirth = this.dateConverterService.convertDate(
      insuredControl.dateOfBirth
    );
    insured.isSmoker = insuredControl.isSmoker === OptionsEnum.Yes;
    insured.covers[0].sumAssured = replaceThousandSeparatorAndRemovingEuroPrefix(insuredControl.sumAssured);
    insured.covers[0].startDate = this.dateConverterService.convertDate(this.minDate);
    insured.covers[0].endDate = new Date(this.minDate);

    insured.covers[0].endDate.setFullYear(
      insured.covers[0].endDate.getFullYear() +
      Number(this.formGroup.value.numberOfYears)
    );
    insured.covers[0].endDate.setDate(insured.covers[0].endDate.getDate() - 1);
    insured.covers[0].variables.typeOfCover = {
      type: 'string',
      value: this.formGroup.value.typeOfCover,
    };

    insured.covers[0].variables.annuityRate = {
      type: 'decimal',
      value: this.formGroup.value.annuityRate.replace(',', '.'),
    };
    calculationRequestDto.insureds.push(insured);
  }

  clearOffersData(): void {
    this.offersDataSource = null;
  }

  saveCurrentCalculationFormValues(): void {
    this.sessionOrvService.setCalculationFormValues(this.formGroup.value);
    this.componentCommunicationService.changeSum.emit(this.formGroup.value.secondInsured.sumAssured);
  }

  doCalculation(): void {
    if (this.isFormValid()) {
      this.saveCurrentCalculationFormValues();
      this.clearOffersData();
      this.notificationEvent = {
        type: NOTIFICATION_TYPE.HIDDEN,
      };

      const calculationRequestDto = new CalculationRequestDto(ProductTypeEnum.ORV, PolicyTerms.QL_ORV_2020_01);

      // Setup 1st insured
      this.setupInsured(calculationRequestDto, this.formGroup.value.insured);

      // Setup 2nd insured
      if (this.formGroup.value.secondInsuredPresent === OptionsEnum.Yes) {
        this.setupInsured(calculationRequestDto, this.formGroup.getRawValue().secondInsured);
      }

      // Setup calculation variables
      if (calculationRequestDto.typeOfCover === CoverTypeEnum.ANNUITY_DECREASING) {
        calculationRequestDto.variables.annuityRate = {
          type: 'decimal',
          value: this.formGroup.value.annuityRate.replace(',', '.'),
        };
      }

      calculationRequestDto.variables.paymentMethod = {
        type: 'string',
        value: this.MONTHLY_PAYMENT_METHOD,
      };

      calculationRequestDto.variables.typeOfCover = {
        type: 'string',
        value: this.formGroup.value.typeOfCover,
      };
      calculationRequestDto.annuityRate = this.formGroup.value.annuityRate.replace(',', '.');
      calculationRequestDto.dateOfCommencement = this.minDate;
      calculationRequestDto.numberOfYears = this.formGroup.value.numberOfYears;

      this.sessionService.setCalculationRequest(calculationRequestDto);
      this.calculationApiService
        .requestCalculation(calculationRequestDto)
        .subscribe((response) => {
          this.offersDataSource = [];
          if (response?.results != null && response.results.length > 0) {
            const premiumsFromResponse = response.results[0].premiums;
            this.premiums = premiumsFromResponse[0];
            premiumsFromResponse.forEach((premiums) => {
              const premiumInsurer = this.getInsurerTranslation(premiums.insurer);
              this.offersDataSource.push({
                insurer: premiums.insurer,
                insurerName: premiumInsurer.text,
                premium: premiums.premium,
                policyTerms: premiums.policyTerms,
                insured1Amount: premiums.insured1Amount,
                insured2Amount: premiums.insured2Amount,
                insured1premium: premiums.insured1premium,
                insured2premium: premiums.insured2premium,
              });
            });
            this.getSelectPremium(this.premiums);
          }
        });
    }
  }
  private getInsurerTranslation(insurer: string): BaseOption {
    const index = this.premiumsInsurerList.findIndex((i) => i.value === insurer);
    return this.premiumsInsurerList[index];
  }

  downloadQuote(): void {
    const quotationRequestDto = new QuotationRequestDto();
    quotationRequestDto.calculationRequestDto = this.sessionService.getCallculationRequest();
    quotationRequestDto.premiumResponse = this.premiums;
    quotationRequestDto.intermediaryName = this.sessionService.intermediaryName;
    quotationRequestDto.intermediaryEmail = this.sessionService.intermediaryEmail;
    quotationRequestDto.intermediaryPhone = this.sessionService.intermediaryPhone;
    this.calculationApiService
      .getQuotation(quotationRequestDto)
      .subscribe((response) => {
        const byteCharacters = response.pdfContent;
        openPDFFromByteCharacters(byteCharacters, 'Offerte.pdf');
      });
  }

  applicationResult(): void {
    this.navigationSettings.setFinishedState(StepsORV.CALCULATE, true);
    this.navigationSettings.setActiveState(StepsORV.CALCULATE, false);
    this.sessionService.setCalculationResponse(this.premiums);
  }

  get annuityRateFormat(): string {
    return this.translationService.currentLang === 'en' ? this.annuityFormat = '0*.00' :
     this.annuityFormat = '0*,00';
  }

  openPopup(additionalInfo: string, insurer?: string): void {
    this.componentCommunicationService.loadDialog(additionalInfo, insurer);
  }

  isPremiumsCalculated(): boolean {
    return this.offersDataSource !== null;
  }

  isSecondInsured(): boolean {
    return this.formGroup.value.secondInsuredPresent === OptionsEnum.Yes;
  }
}
